import React, { useState, useEffect } from 'react';
import {
  sendNotification,
  listenToNotifications,
  getNotificationStats,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  type NotificationStats,
} from '../../services/notificationService';
import {
  generateWhatsAppShareLink,
  getWhatsAppConfig,
  sendToWhatsAppChannel,
  type WhatsAppConfig,
} from '../../services/whatsappService';
import {
  getFileIcon,
  getFileColor,
  formatFileSize,
  type UploadedFile,
} from '../../services/fileUploadService';
import SendAnnouncementForm from './SendAnnouncementForm';
import WhatsAppSettings from './WhatsAppSettings';

interface AnnouncementDashboardProps {
  domain: string;
  onBack: () => void;
}

interface Announcement {
  id: string;
  title: string;
  content: string;
  senderName: string;
  senderRole: string;
  targetClass: string;
  priority: 'low' | 'medium' | 'high';
  category: string;
  createdAt: number;
  read: boolean;
  attachments?: UploadedFile[];
}

const AnnouncementDashboard: React.FC<AnnouncementDashboardProps> = ({ domain, onBack }) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'send' | 'whatsapp'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterClass, setFilterClass] = useState<string>('all');

  // WhatsApp state
  const [whatsAppConfig, setWhatsAppConfig] = useState<WhatsAppConfig | null>(null);
  const [sharingToWhatsApp, setSharingToWhatsApp] = useState<string | null>(null);

  // Get current user info from localStorage
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userUID = localStorage.getItem('userUID') || '';
  const userName = userData.name || 'Admin';
  const userRole = userData.role || 'admin';

  useEffect(() => {
    if (!domain) return;

    // Listen to announcements in real-time
    const unsubscribe = listenToNotifications(domain, (notifs) => {
      setAnnouncements(notifs);
      setLoading(false);
    });

    // Get stats
    loadStats();

    // Load WhatsApp config
    loadWhatsAppConfig();

    return () => unsubscribe(); // Cleanup listener
  }, [domain]);

  const loadStats = async () => {
    const statsData = await getNotificationStats(domain);
    setStats(statsData);
  };

  const loadWhatsAppConfig = async () => {
    const config = await getWhatsAppConfig(domain);
    setWhatsAppConfig(config);
  };

  const handleSendAnnouncement = async (formData: any) => {
    const result = await sendNotification(domain, {
      title: formData.title,
      message: formData.message,
      targetClass: formData.targetClass,
      senderId: userUID,
      senderName: userName,
      senderRole: userRole,
      priority: formData.priority,
      category: formData.category,
      attachments: formData.attachments,
    });

    if (result.success) {
      alert('✅ Announcement sent successfully!');
      setActiveTab('all');
      loadStats(); // Refresh stats
    } else {
      alert('❌ Failed to send announcement: ' + result.error);
    }
  };

  const handleMarkAsRead = async (announcementId: string) => {
    await markNotificationAsRead(domain, announcementId);
    loadStats(); // Refresh stats
  };

  const handleMarkAllAsRead = async () => {
    if (confirm('Mark all announcements as read?')) {
      await markAllNotificationsAsRead(domain);
      loadStats(); // Refresh stats
    }
  };

  const handleShareToWhatsApp = (announcement: Announcement) => {
    const shareLink = generateWhatsAppShareLink({
      title: announcement.title,
      content: announcement.content,
      category: announcement.category,
      attachments: announcement.attachments?.map((a) => ({
        originalName: a.originalName,
        url: a.url,
      })),
    });
    window.open(shareLink, '_blank');
  };

  const handlePostToWhatsAppChannel = async (announcement: Announcement) => {
    if (!whatsAppConfig?.enabled) {
      alert('WhatsApp integration is not configured. Please set it up in WhatsApp Settings.');
      return;
    }

    setSharingToWhatsApp(announcement.id);

    const result = await sendToWhatsAppChannel(domain, {
      title: announcement.title,
      content: announcement.content,
      category: announcement.category,
      priority: announcement.priority,
    });

    setSharingToWhatsApp(null);

    if (result.success) {
      alert('✅ Posted to WhatsApp Channel successfully!');
    } else {
      alert('❌ Failed to post to WhatsApp: ' + result.error);
    }
  };

  const getPriorityBadge = (priority: string) => {
    const badges = {
      low: 'badge bg-success',
      medium: 'badge bg-warning text-dark',
      high: 'badge bg-danger',
    };
    return badges[priority as keyof typeof badges] || 'badge bg-secondary';
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      announcement: 'bi-megaphone',
      teacher_announcement: 'bi-person-video3',
      fee: 'bi-currency-rupee',
      exam: 'bi-clipboard-check',
      assignment: 'bi-journal-text',
      result: 'bi-trophy',
    };
    return icons[category] || 'bi-bell';
  };

  const filteredAnnouncements = announcements.filter((announce: Announcement) => {
    if (filterCategory !== 'all' && announce.category !== filterCategory) return false;
    if (filterClass !== 'all' && announce.targetClass !== filterClass) return false;
    return true;
  });

  const uniqueClasses = Array.from(new Set(announcements.map((n: Announcement) => n.targetClass)));

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <div className="bg-primary-custom text-white p-3 shadow-sm">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <h4 className="mb-0">
            <i className="bi bi-megaphone-fill me-2"></i>Announcements
          </h4>
          <button className="btn btn-light btn-sm" onClick={onBack}>
            <i className="bi bi-arrow-left me-2"></i>Back to Dashboard
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow-sm">
        <div className="container-fluid">
          <ul className="nav nav-tabs border-0 flex-nowrap overflow-auto">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
              >
                <i className="bi bi-list-ul me-2"></i>All Announcements
                {stats && stats.unreadCount > 0 && (
                  <span className="badge bg-danger ms-2">{stats.unreadCount}</span>
                )}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'send' ? 'active' : ''}`}
                onClick={() => setActiveTab('send')}
              >
                <i className="bi bi-send me-2"></i>Send Announcement
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'whatsapp' ? 'active' : ''}`}
                onClick={() => setActiveTab('whatsapp')}
              >
                <i className="bi bi-whatsapp me-2 text-success"></i>WhatsApp Settings
                {whatsAppConfig?.enabled && (
                  <span className="badge bg-success ms-2">Active</span>
                )}
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="container-fluid py-4">
        {/* Stats Cards */}
        {stats && activeTab === 'all' && (
          <div className="row mb-4">
            <div className="col-6 col-md-3">
              <div className="card text-center shadow-sm">
                <div className="card-body">
                  <h3 className="text-primary">{stats.total}</h3>
                  <p className="mb-0 text-muted small">Total</p>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card text-center shadow-sm">
                <div className="card-body">
                  <h3 className="text-danger">{stats.unreadCount}</h3>
                  <p className="mb-0 text-muted small">Unread</p>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card text-center shadow-sm">
                <div className="card-body">
                  <h3 className="text-warning">{stats.last24Hours}</h3>
                  <p className="mb-0 text-muted small">Last 24h</p>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card text-center shadow-sm">
                <div className="card-body">
                  <button
                    className="btn btn-sm btn-outline-primary w-100"
                    onClick={handleMarkAllAsRead}
                    disabled={stats.unreadCount === 0}
                  >
                    <i className="bi bi-check-all me-1"></i>
                    Mark All Read
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {activeTab === 'send' ? (
          <SendAnnouncementForm
            domain={domain}
            onSubmit={handleSendAnnouncement}
            onCancel={() => setActiveTab('all')}
          />
        ) : activeTab === 'whatsapp' ? (
          <WhatsAppSettings
            domain={domain}
            onClose={() => {
              setActiveTab('all');
              loadWhatsAppConfig(); // Refresh config after settings change
            }}
          />
        ) : (
          <>
            {/* Filters */}
            <div className="card shadow-sm mb-3">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="bi bi-funnel me-2"></i>Filter by Category
                    </label>
                    <select
                      className="form-select"
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                    >
                      <option value="all">All Categories</option>
                      <option value="announcement">General Announcement</option>
                      <option value="teacher_announcement">Teacher Announcement</option>
                      <option value="fee">Fee Related</option>
                      <option value="exam">Exam Related</option>
                      <option value="assignment">Assignment</option>
                      <option value="result">Result</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="bi bi-funnel me-2"></i>Filter by Class
                    </label>
                    <select
                      className="form-select"
                      value={filterClass}
                      onChange={(e) => setFilterClass(e.target.value)}
                    >
                      <option value="all">All Classes</option>
                      {uniqueClasses.map((cls) => (
                        <option key={cls} value={cls}>
                          {cls}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Announcements List */}
            <div className="card shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  Announcements ({filteredAnnouncements.length})
                </h6>
                {whatsAppConfig?.enabled && (
                  <span className="badge bg-success">
                    <i className="bi bi-whatsapp me-1"></i>
                    WhatsApp Active
                  </span>
                )}
              </div>
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : filteredAnnouncements.length === 0 ? (
                  <div className="text-center p-5 text-muted">
                    <i className="bi bi-inbox display-1"></i>
                    <p className="mt-3">No announcements found</p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {filteredAnnouncements.map((announce: Announcement) => (
                      <div
                        key={announce.id}
                        className={`list-group-item ${!announce.read ? 'bg-light' : ''}`}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div
                            className="flex-grow-1"
                            onClick={() => !announce.read && handleMarkAsRead(announce.id)}
                            style={{ cursor: announce.read ? 'default' : 'pointer' }}
                          >
                            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                              <i className={`bi ${getCategoryIcon(announce.category)} text-primary`}></i>
                              <h6 className="mb-0">{announce.title}</h6>
                              {!announce.read && (
                                <span className="badge bg-primary">New</span>
                              )}
                              <span className={getPriorityBadge(announce.priority)}>
                                {announce.priority.toUpperCase()}
                              </span>
                            </div>
                            <p className="mb-2 text-muted">{announce.content}</p>

                            {/* Attachments Display */}
                            {announce.attachments && announce.attachments.length > 0 && (
                              <div className="mb-2">
                                <small className="text-muted d-block mb-1">
                                  <i className="bi bi-paperclip me-1"></i>
                                  Attachments ({announce.attachments.length})
                                </small>
                                <div className="d-flex flex-wrap gap-2">
                                  {announce.attachments.map((file) => (
                                    <a
                                      key={file.id}
                                      href={file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                      onClick={(e) => e.stopPropagation()}
                                      title={`${file.originalName} (${formatFileSize(file.size)})`}
                                    >
                                      <i
                                        className={`bi ${getFileIcon(file.type)}`}
                                        style={{ color: getFileColor(file.type) }}
                                      ></i>
                                      <span className="text-truncate" style={{ maxWidth: '120px' }}>
                                        {file.originalName}
                                      </span>
                                      <small className="text-muted">({formatFileSize(file.size)})</small>
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}

                            <small className="text-muted">
                              <i className="bi bi-person-circle me-1"></i>
                              {announce.senderName} ({announce.senderRole})
                              <span className="mx-2">•</span>
                              <i className="bi bi-calendar me-1"></i>
                              {new Date(announce.createdAt).toLocaleString()}
                              <span className="mx-2">•</span>
                              <i className="bi bi-people me-1"></i>
                              {announce.targetClass}
                            </small>
                          </div>

                          {/* Action Buttons */}
                          <div className="d-flex gap-1 ms-2 flex-shrink-0">
                            {/* Share to WhatsApp Button */}
                            <div className="dropdown">
                              <button
                                className="btn btn-sm btn-outline-success"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                title="Share to WhatsApp"
                              >
                                <i className="bi bi-whatsapp"></i>
                              </button>
                              <ul className="dropdown-menu dropdown-menu-end">
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => handleShareToWhatsApp(announce)}
                                  >
                                    <i className="bi bi-share me-2"></i>
                                    Share via WhatsApp
                                  </button>
                                </li>
                                {whatsAppConfig?.enabled && (
                                  <li>
                                    <button
                                      className="dropdown-item"
                                      onClick={() => handlePostToWhatsAppChannel(announce)}
                                      disabled={sharingToWhatsApp === announce.id}
                                    >
                                      {sharingToWhatsApp === announce.id ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-2"></span>
                                          Posting...
                                        </>
                                      ) : (
                                        <>
                                          <i className="bi bi-broadcast me-2"></i>
                                          Post to Channel
                                        </>
                                      )}
                                    </button>
                                  </li>
                                )}
                              </ul>
                            </div>

                            {/* Mark as Read Button */}
                            {!announce.read && (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleMarkAsRead(announce.id)}
                                title="Mark as Read"
                              >
                                <i className="bi bi-check2"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnnouncementDashboard;
